import { useMemo, useState } from 'react'
import { ReactComponent as QuestionIcon } from '../assets/images/icons/ic-question-circle.svg'
import { ReactComponent as SearchIcon } from '../assets/images/icons/ic-search.svg'
import Dialog from './dialogs/Dialog'
import TooltipWrapper from './TooltipWrapper'
import styles from './LeadFlowHelpDialog.module.css'

export const LEAD_FLOW_STEPS = [
    { number: '01', title: 'Nuovo', text: 'Arriva la candidatura, nasce l’opportunità e viene assegnato il primo task.' },
    { number: '02', title: 'In contatto', text: 'Chiamate, WhatsApp ed email alimentano attività, feedback e prossime azioni.' },
    { number: '03', title: 'Qualifica', text: 'Il sales verifica interesse, fit e tempistiche con il percorso guidato.' },
    { number: '04', title: 'Chiusura', text: 'Il lead qualificato riceve il link di pagamento e viene seguito fino all’acquisto.' },
    { number: '05', title: 'Esito', text: 'Pagamento, perdita o riciclo chiudono il ciclo senza cancellarne la cronologia.' },
]

export const LEAD_HELP_SECTIONS = [
    {
        id: 'daily-work',
        label: 'Lavorazione quotidiana',
        items: [
            {
                question: 'Da dove comincio quando arriva un nuovo lead?',
                answer: 'Apri il task assegnato nell’agenda commerciale. La prima azione è normalmente una chiamata: puoi avviarla con Aircall oppure registrare manualmente il contatto. Usa sempre il task per l’azione prevista, così il sistema può proporre correttamente quella successiva.',
                keywords: 'nuovo lead prima chiamata agenda task',
            },
            {
                question: 'Cosa succede se il lead non risponde?',
                answer: 'Registra o lascia arrivare da Aircall la chiamata senza risposta. La sequenza automatica pianifica i tentativi successivi e, quando previsto, WhatsApp ed email di ricontatto. Non creare manualmente un duplicato del task suggerito dal sistema.',
                keywords: 'non risponde tentativi seconda terza chiamata email whatsapp',
            },
            {
                question: 'Come registro un’attività extra che non deve completare un task?',
                answer: 'Nel dettaglio dell’opportunità usa “Registra attività commerciale”. Puoi registrare una chiamata, un messaggio WhatsApp, un’email o un SMS aggiuntivo senza chiudere i task ancora aperti.',
                keywords: 'attività manuale extra contatto senza completare task',
            },
            {
                question: 'Ho inviato un WhatsApp e sono in attesa: come registro la risposta?',
                answer: 'Registra prima il messaggio come inviato e in attesa di risposta. Quando il lead risponde, riapri l’attività in attesa e registra la risposta con il relativo esito commerciale. In questo modo rimane una sola conversazione coerente nella cronologia.',
                keywords: 'whatsapp inviato risposta attesa aggiornare conversazione',
            },
            {
                question: 'Come distinguo un follow-up da una chiamata schedulata?',
                answer: 'Il follow-up è un ricontatto generico, che può avvenire su canali diversi. Usa “Chiamata schedulata” quando hai concordato con il lead un giorno e un’ora per sentirvi. I task modificabili permettono di cambiare tipo, scadenza e sales assegnato.',
                keywords: 'follow up chiamata pianificata schedulata appuntamento scadenza',
            },
        ],
    },
    {
        id: 'agenda',
        label: 'Agenda e viste',
        items: [
            {
                question: 'Come funziona la vista Calendario?',
                answer: 'Il calendario carica tutti i task del mese visualizzato. Seleziona un giorno nella parte sinistra per vedere a destra le attività previste, con lead, corso, scadenza e tipologia.\n\nI contrassegni colorati sotto il calendario mostrano quanti task appartengono a ciascun gruppo, per esempio chiamate, follow-up, WhatsApp, email e pagamenti. Cliccandoli puoi mostrare o nascondere una o più tipologie.',
                keywords: 'agenda vista calendario mese giorno badge contrassegni colori chiamate follow up whatsapp email pagamenti',
            },
            {
                question: 'I filtri del Calendario ricaricano ogni volta i task?',
                answer: 'No. I task vengono scaricati quando apri o cambi mese. I contrassegni colorati filtrano in locale i dati già caricati, quindi puoi combinarli liberamente senza avviare una nuova richiesta. Il sistema ricarica l’agenda soltanto quando passi a un mese diverso o aggiorni esplicitamente la pagina.',
                keywords: 'calendario filtri locale chiamata api cambio mese badge multipli',
            },
            {
                question: 'Quando conviene usare la vista Lista?',
                answer: 'Usa la Lista per lavorare operativamente una coda di attività. Puoi combinare i filtri di scadenza — Scaduti, Oggi e Prossimi giorni — con uno o più tipi di task, come chiamate schedulate, follow-up, prima o seconda chiamata, WhatsApp, email e link di pagamento.\n\nLa lista mostra cinque task per pagina e permette di aprire direttamente l’opportunità corrispondente.',
                keywords: 'vista lista coda task filtri scaduti oggi prossimi giorni cinque pagina attività multiple',
            },
            {
                question: 'In che ordine vedo i task scaduti?',
                answer: 'I task scaduti sono ordinati dal più recente al più vecchio, così trovi per primi quelli appena diventati urgenti. I task futuri seguono invece la scadenza più vicina. Puoi restringere la lista selezionando il filtro “Scaduti”.',
                keywords: 'ordine task scaduti inverso recente vecchio urgenza scadenza',
            },
            {
                question: 'La vista scelta viene ricordata?',
                answer: 'Sì. Calendario è la visualizzazione iniziale predefinita; quando passi alla Lista o torni al Calendario, la scelta viene salvata nel browser e ripristinata al prossimo accesso dallo stesso dispositivo.',
                keywords: 'vista predefinita ricordata localstorage calendario lista browser dispositivo',
            },
            {
                question: 'Posso modificare un task dall’agenda?',
                answer: 'Apri il task e usa “Modifica”. Per follow-up e chiamate schedulate puoi cambiare il tipo, la scadenza e il sales assegnato. La modifica riguarda quel task: non cambia automaticamente l’owner dell’intera opportunità.',
                keywords: 'modifica task agenda tipo follow up chiamata schedulata scadenza sales assegnato owner',
            },
        ],
    },
    {
        id: 'aircall',
        label: 'Aircall e canali',
        items: [
            {
                question: 'Cosa registra automaticamente Aircall?',
                answer: 'I webhook registrano chiamate in entrata e in uscita, risposta o mancata risposta, durata, registrazione e tag disponibili. Una chiamata con risposta crea un task di feedback: i tag intelligenti precompilano il suggerimento, ma il sales deve sempre verificarlo e può correggerlo.',
                keywords: 'aircall webhook chiamata ingresso uscita tag feedback registrazione',
            },
            {
                question: 'I tag Aircall confermano automaticamente l’esito commerciale?',
                answer: 'No. I tag sono un suggerimento e precompilano il feedback. La decisione resta al sales, che può modificare esito e obiezione prima di salvare. Gli automatismi definitivi partono dal feedback confermato.',
                keywords: 'smart tag automatic call tagging conferma correggere esito',
            },
            {
                question: 'Cosa faccio se il numero è errato o il lead non usa WhatsApp?',
                answer: 'Nel task di contatto scegli “Segnala un problema”. Per un numero errato vengono annullati i task telefonici e WhatsApp incompatibili e parte l’email automatica con il link per correggerlo. Quando il lead aggiorna il numero, viene creato un nuovo task di ricontatto. Se manca solo WhatsApp, il flusso continua sugli altri canali.',
                keywords: 'numero sbagliato inesistente whatsapp non disponibile correzione email',
            },
        ],
    },
    {
        id: 'qualification',
        label: 'Qualifica ed esiti',
        items: [
            {
                question: 'Quando devo avviare la qualifica?',
                answer: 'Dopo un contatto riuscito e significativo. Il feedback della conversazione può suggerire automaticamente l’avvio della qualifica; in alternativa puoi avviarla dalle decisioni commerciali disponibili. Non serve qualificare una semplice chiamata senza risposta.',
                keywords: 'avvia qualifica risposta contatto riuscito automatico',
            },
            {
                question: 'Cosa significano SAL, LQ e non qualificato?',
                answer: 'SAL significa che il lead è stato preso in carico e accettato dal sales. LQ indica che la valutazione commerciale ha confermato interesse e compatibilità. “Non qualificato” è un attributo della qualifica: il lead resta distinguibile nella fase di qualifica e non equivale automaticamente a una vendita persa.',
                keywords: 'sal lq accettato sales lead qualificato non qualificato',
            },
            {
                question: 'Qual è la differenza tra non qualificato e perso?',
                answer: '“Non qualificato” descrive un lead che non supera la valutazione commerciale, per esempio perché fuori target. “Perso” descrive invece un’opportunità già lavorata che non procede più. Per segnare una perdita il lead passa prima dalla qualifica; il sistema propone la classificazione coerente con fase e motivazione.',
                keywords: 'perso non qualificato fuori target non interessato differenza',
            },
            {
                question: 'Come gestisco chi vuole la prossima edizione?',
                answer: 'Nel percorso guidato scegli “Prossima edizione” e indica la data di riciclo. L’opportunità viene posticipata e il sistema crea il task di ripresa alcuni giorni prima della data prevista.',
                keywords: 'prossima edizione posticipato riciclo nurture data',
            },
            {
                question: 'Cosa indicano le temperature Hot, Warm, Cooling e Cold?',
                answer: 'La temperatura misura da quanto tempo il nuovo lead attende la prima lavorazione: Hot sotto i 15 minuti, Warm tra 15 minuti e 4 ore, Cooling tra 4 e 24 ore, Cold oltre 24 ore. Serve a dare priorità ai contatti più recenti; non sostituisce la qualifica commerciale.',
                keywords: 'temperatura hot warm cooling cold priorità tempo sla',
            },
            {
                question: 'Posso recuperare un lead segnato come perso?',
                answer: 'Sì. Il lead e la sua cronologia non vengono eliminati. Apri l’opportunità chiusa e usa le azioni disponibili per riaprirla, riciclarla o registrare nuove attività se il lead cambia idea o la chiusura era errata.',
                keywords: 'recuperare riaprire perso errore cambia idea',
            },
        ],
    },
    {
        id: 'payments',
        label: 'Pagamenti e ordini',
        items: [
            {
                question: 'Come ottengo il link di pagamento?',
                answer: 'Il link viene proposto al termine della valutazione guidata quando interesse, compatibilità con il corso e tempistiche sono confermati, il budget è compatibile e non ci sono obiezioni che richiedono prima un follow-up.\n\nDopo aver confermato la valutazione, l’opportunità rimane nello stage “Qualifica” con qualifica “Lead qualificato (LQ)” e viene creato il task “Invia link di pagamento”. Aprendo il task trovi il checkout personalizzato per corso ed email, con il pulsante “Copia link”.\n\nPuoi inviarlo manualmente via WhatsApp o email oppure scegliere l’invio automatico. Quando il task viene completato, l’opportunità passa allo stage “Chiusura” e mantiene la qualifica LQ.',
                keywords: 'link pagamento checkout copia invia task whatsapp email',
            },
            {
                question: 'Cosa invia l’opzione automatica del task di pagamento?',
                answer: 'Se non esiste un ordine rateale collegato, il sistema invia via email il link al checkout. Se esiste un ordine rateale per quel prodotto, invia invece il link per pagare la prima rata. L’invio e il suo stato rimangono tracciati nel task.',
                keywords: 'mail automatica checkout prima rata ordine rateale',
            },
            {
                question: 'Come setto il piano rateale per un utente che non ha un ordine?',
                answer: 'Apri il profilo dell’utente, iscrivilo al corso e seleziona “Crea un ordine per questa iscrizione”. Nella sezione degli ordini dell’utente clicca quindi “Mostra tutti”: troverai il nuovo ordine in attesa. Aprilo, entra in modifica e genera il piano indicando numero di rate, frequenza e prima scadenza. Controlla che la somma delle rate coincida con il totale dell’ordine e salva. Quando l’ordine è collegato all’opportunità, il task di pagamento utilizzerà il link della prima rata.',
                keywords: 'piano rateale senza ordine profilo utente iscrivi corso crea ordine iscrizione mostra tutti ordine in attesa rate frequenza scadenza',
            },
            {
                question: 'Quando un’opportunità diventa vinta automaticamente?',
                answer: 'L’opportunità collegata diventa vinta quando l’ordine raggiunge lo stato “Pagato” oppure, per un ordine rateale, quando la prima rata risulta pagata. Il sales può comunque segnare manualmente la vittoria quando necessario.',
                keywords: 'vinto automaticamente ordine succeeded pagato prima rata',
            },
            {
                question: 'Dove vedo ordine e rate del lead?',
                answer: 'Nel dettaglio dell’opportunità trovi la card “Ordine collegato” con totale, stato, metodo e rate. “Apri ordine” porta alla gestione completa, dove puoi verificare scadenze, pagamenti e configurazione del piano.',
                keywords: 'dettagli ordine rate stato metodo apri ordine',
            },
        ],
    },
    {
        id: 'ownership',
        label: 'Assegnazione e dati',
        items: [
            {
                question: 'Se completo il task di un altro sales, a chi va il task successivo?',
                answer: 'La cronologia registra che hai completato tu l’attività, ma il nuovo task resta assegnato all’owner dell’opportunità. Se deve occuparsene stabilmente un’altra persona, modifica l’owner; se cambia solo quella singola attività, modifica il sales assegnato al task.',
                keywords: 'owner assegnatario task altro sales riassegnare',
            },
            {
                question: 'Come modifico nome, email o telefono del lead?',
                answer: 'Usa “Modifica contatto” nel dettaglio dell’opportunità. Nome, cognome e telefono vengono sincronizzati anche sul contatto Aircall. L’email può essere cambiata solo quando l’utente non ha un profilo associato, per evitare di alterare un account registrato.',
                keywords: 'modifica contatto nome cognome email telefono aircall profilo',
            },
            {
                question: 'Come creo manualmente un’opportunità?',
                answer: 'Usa “Nuova opportunità” in alto nella pagina. Cerca prima l’utente per email, completa gli eventuali dati mancanti e seleziona il corso. Il sistema evita di creare duplicati incompatibili e avvia il nuovo flusso commerciale.',
                keywords: 'creare manualmente nuova opportunità candidatura email corso',
            },
        ],
    },
]

const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const LeadFlowHelpDialog = () => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const normalizedQuery = normalize(query)

    const sections = useMemo(() => LEAD_HELP_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => normalize([
            section.label,
            item.question,
            item.answer,
            item.keywords,
        ].join(' ')).includes(normalizedQuery)),
    })).filter((section) => section.items.length > 0), [normalizedQuery])

    const resultCount = sections.reduce((total, section) => total + section.items.length, 0)

    const close = () => {
        setOpen(false)
        setQuery('')
    }

    return <>
        {!open && <TooltipWrapper text="Guida al flusso commerciale">
            <button type="button" className={styles.launcher} aria-label="Apri la guida al flusso commerciale" onClick={() => setOpen(true)}>
                <QuestionIcon />
            </button>
        </TooltipWrapper>}
        <Dialog
            appearance="modern"
            open={open}
            onClose={close}
            title="Guida al flusso commerciale"
            style={{ width: 'min(900px, 96vw)', height: 'min(760px, 92dvh)', maxHeight: '92dvh' }}
            contentMaxHeight="none"
            overflow="hidden"
            contentStyle={{ padding: 0, alignItems: 'stretch' }}
        >
            <div className={styles.guide}>
                <header className={styles.intro}>
                    <div>
                        <span>Manuale operativo sales</span>
                        <p>Cerca una domanda oppure consulta il percorso completo dalla candidatura alla chiusura.</p>
                    </div>
                    <label className={styles.search}>
                        <SearchIcon aria-hidden="true" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Cerca: pagamento, Aircall, qualifica…"
                            aria-label="Cerca nella guida"
                        />
                    </label>
                </header>

                <div className={styles.scrollArea}>
                    {!normalizedQuery && <section className={styles.flow} aria-labelledby="lead-flow-title">
                        <div className={styles.sectionHeading}>
                            <div><span>Il percorso</span><h2 id="lead-flow-title">Come si muove un lead</h2></div>
                            <p>Le attività registrate e i feedback determinano task e azioni disponibili.</p>
                        </div>
                        <ol>{LEAD_FLOW_STEPS.map((step) => <li key={step.number}>
                            <span>{step.number}</span>
                            <div><strong>{step.title}</strong><p>{step.text}</p></div>
                        </li>)}</ol>
                    </section>}

                    <section className={styles.faq} aria-labelledby="faq-title">
                        <div className={styles.sectionHeading}>
                            <div><span>{normalizedQuery ? `${resultCount} risultati` : 'FAQ operative'}</span><h2 id="faq-title">Domande frequenti</h2></div>
                        </div>
                        {sections.map((section) => <div className={styles.faqSection} key={section.id}>
                            <h3>{section.label}</h3>
                            <div className={styles.questions}>{section.items.map((item) => <details key={item.question} open={normalizedQuery ? true : undefined}>
                                <summary><span>{item.question}</span><span aria-hidden="true">+</span></summary>
                                <div className={styles.answer}>{item.answer.split('\n\n').map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
                            </details>)}</div>
                        </div>)}
                        {!resultCount && <div className={styles.empty}>
                            <QuestionIcon />
                            <strong>Nessun risultato</strong>
                            <p>Prova con termini come “pagamento”, “Aircall”, “perso” o “task”.</p>
                            <button type="button" onClick={() => setQuery('')}>Cancella ricerca</button>
                        </div>}
                    </section>
                </div>
            </div>
        </Dialog>
    </>
}

export default LeadFlowHelpDialog
